import {
  Assessment,
  AssessmentAverageGroupTypeEnum,
  AssessmentAverageResult,
  AssessmentAverageSubjectResult,
  AssessmentGroup,
  AssessmentMethodType,
  GetMarkbookSortBy,
  GroupType,
  MarkbookAssessmentEntry,
} from '@schooly/api';

import {
  AverageResultsAssessmentColumnData,
  AverageResultsAverageColumnData,
  AverageResultsColumnData,
} from './AverageResults';
import { MarkbookBaseline, MarkbookBaselineConfig, MarkbookSubject } from './types';

export const MARKBOOK_OTHER_SUBJECT_ID = 'other';

export const MARKBOOK_OTHER_SUBJECT = {
  id: MARKBOOK_OTHER_SUBJECT_ID,
  labelId: 'assessments-markbook-Other',
};
export const MARKBOOK_TUTOR_GROUP = { id: 'tutor_group', labelId: 'groups-TutorGroup' };

export const getMarkbookSubject = (
  group: Pick<AssessmentGroup, 'id' | 'name' | 'group_type' | 'subject'>,
): MarkbookSubject => {
  const subjectGroup = { id: group.id, name: group.name };
  const noSubject =
    group.group_type === GroupType.TutorGroup ? MARKBOOK_TUTOR_GROUP : MARKBOOK_OTHER_SUBJECT;
  const subject = group.subject ?? noSubject;
  return { ...subject, groups: [subjectGroup] };
};

export const getMarkbookSubjects = (groups: AssessmentGroup[]): MarkbookSubject[] => {
  const subjects = groups.reduce<[Array<MarkbookSubject>, Set<string>]>(
    (acc, group) => {
      const subjectGroup = { id: group.id, name: group.name };
      const noSubject =
        group.group_type === GroupType.TutorGroup ? MARKBOOK_TUTOR_GROUP : MARKBOOK_OTHER_SUBJECT;
      const subject = group.subject ?? noSubject;

      if (acc[1].has(subject.id)) {
        const subjectItem = acc[0].find((s) => s.id === subject.id);

        if (subjectItem && !subjectItem?.groups.some((g) => g.id === subjectGroup.id)) {
          subjectItem.groups.push(subjectGroup);
        }
      } else {
        acc[1].add(subject.id);
        acc[0].push({ ...subject, groups: [subjectGroup] });
      }
      return acc;
    },
    [[], new Set<string>()],
  )[0];

  // Sort subjects: Tutor Group first, Other last, regular subjects in between
  return subjects.sort((a, b) => {
    if (a.id === MARKBOOK_TUTOR_GROUP.id) return -1;
    if (b.id === MARKBOOK_TUTOR_GROUP.id) return 1;
    if (a.id === MARKBOOK_OTHER_SUBJECT.id) return 1;
    if (b.id === MARKBOOK_OTHER_SUBJECT.id) return -1;
    return (a.name || '').localeCompare(b.name || '');
  });
};

export const hasMultipleGroupsEntriesInCell = (entries: MarkbookAssessmentEntry[]) => {
  const uniqueGroupIds = new Set(entries.map((entry) => entry.group.id));
  return uniqueGroupIds.size > 1;
};

export const sortByGroupName = <T extends { group: { name: string } }>(items: T[]) => {
  return items.sort((a, b) => {
    const groupA = a.group;
    const groupB = b.group;
    return groupA.name.localeCompare(groupB.name);
  });
};

export const getMethodSortBy = (type: AssessmentMethodType): GetMarkbookSortBy => {
  switch (type) {
    case AssessmentMethodType.Score:
      return GetMarkbookSortBy.Score;
    case AssessmentMethodType.Grade:
      return GetMarkbookSortBy.Grade;
    case AssessmentMethodType.Comment:
      return GetMarkbookSortBy.Comment;
  }
};

export const averageResultIsForSubject = (
  average: AssessmentAverageResult,
  subject: MarkbookSubject,
) => {
  switch (subject.id) {
    case MARKBOOK_TUTOR_GROUP.id:
      return average.group_type === AssessmentAverageGroupTypeEnum.Value2;
    case MARKBOOK_OTHER_SUBJECT.id:
      return (
        average.group_type === AssessmentAverageGroupTypeEnum.Value1 && average.subject_id === null
      );
    default:
      return average.subject_id === subject.id;
  }
};

export const getAverageResultsCellKey = (data: AverageResultsColumnData) => {
  switch (data.type) {
    case 'assessment': {
      const { assessment, subject, methodId } = data;
      return subject
        ? `${assessment.id}-${subject.id}-${methodId}`
        : `${assessment.id}-${methodId}`;
    }
    case 'average':
      return `${data.subject.id}`;
  }
};

export const findAverageResult = (
  averageData: AssessmentAverageResult[],
  data: AverageResultsAssessmentColumnData,
) => {
  const { assessment, methodId, subject } = data;

  return averageData.find(
    (a) =>
      a.assessment_id === assessment.id &&
      a.method.id === methodId &&
      (!subject || averageResultIsForSubject(a, subject)),
  );
};
export const findAverageSubjectResult = (
  averageData: AssessmentAverageSubjectResult[],
  data: AverageResultsAverageColumnData,
) => {
  const { subject } = data;

  const subjectId = subject.id === MARKBOOK_TUTOR_GROUP.id ? null : subject.id;

  return averageData.find((a) => a.subject_id === subjectId);
};

export const getBaseline = (
  assessments: Assessment[],
  baselineAssessmentConfig: MarkbookBaselineConfig | null,
): MarkbookBaseline | undefined => {
  if (!baselineAssessmentConfig) return;

  switch (baselineAssessmentConfig.kind) {
    case 'assessment':
      const assessment = assessments.find((a) => a.id === baselineAssessmentConfig.assessmentId);
      if (!assessment) return;
      return {
        kind: 'assessment',
        assessment,
        method: baselineAssessmentConfig.method,
        subject: baselineAssessmentConfig.subject,
      };
    case 'average':
      return {
        kind: 'average',
        subject: baselineAssessmentConfig.subject,
      };
  }
};
